/**
 * Calendar-month helpers (YYYY-MM keys, month option lists, month date spans).
 */

import { getDateString, today } from "./core";

/**
 * Count distinct YYYY-MM-DD dates under an aggregate key and each YYYY-MM month.
 *
 * @param {Array<Object>|null|undefined} entries - Rows that include a date key.
 * @param {Object} [options]
 * @param {string} [options.allKey="all"] Bucket key for the full span.
 * @param {(entry: Object) => boolean} [options.includeEntry] Filter; default includes all.
 * @param {(entry: Object) => string|null|undefined} [options.getDateKey] Defaults to ``entry.date``.
 * @returns {Object<string, number>} Map of ``allKey`` / ``YYYY-MM`` → distinct day counts.
 */
export function countDistinctDaysByRangeKey(
  entries,
  {
    allKey = "all",
    includeEntry = () => true,
    getDateKey = (entry) => entry?.date,
  } = {}
) {
  const daysByKey = {
    [allKey]: new Set(),
  };

  (entries || []).forEach((entry) => {
    if (!includeEntry(entry)) return;
    const dateKey = getDateKey(entry);
    if (!dateKey) return;

    daysByKey[allKey].add(dateKey);
    const monthKey = String(dateKey).slice(0, 7);
    if (!daysByKey[monthKey]) {
      daysByKey[monthKey] = new Set();
    }
    daysByKey[monthKey].add(dateKey);
  });

  const counts = {};
  Object.keys(daysByKey).forEach((key) => {
    counts[key] = daysByKey[key].size;
  });
  return counts;
}

/**
 * Return the first and last local ISO dates for a YYYY-MM month key.
 *
 * @param {string} monthKey - Month value in YYYY-MM format.
 * @returns {{start: string, end: string}} Inclusive month date range.
 */
export function getMonthDateRange(monthKey) {
  const [year, month] = monthKey.split("-").map(Number);
  const start = new Date(year, month - 1, 1);
  const end = new Date(year, month, 0);
  return {
    start: getDateString(start),
    end: getDateString(end),
  };
}

/**
 * Build `{ value, label }` month options for every month from start through end
 * (inclusive). Accepts Date objects.
 *
 * @param {Date} startDate - Inclusive range start.
 * @param {Date} endDate - Inclusive range end.
 * @param {Object} [options]
 * @param {string} [options.locale="en-US"] Locale for month labels.
 * @returns {Array<{value: string, label: string}>}
 */
export function buildMonthOptionsBetweenDates(
  startDate,
  endDate,
  { locale = "en-US" } = {}
) {
  if (
    !(startDate instanceof Date) ||
    !(endDate instanceof Date) ||
    Number.isNaN(startDate.getTime()) ||
    Number.isNaN(endDate.getTime()) ||
    startDate > endDate
  ) {
    return [];
  }

  const monthFormatter = new Intl.DateTimeFormat(locale, {
    month: "long",
    year: "numeric",
  });

  const options = [];
  const cursor = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const last = new Date(endDate.getFullYear(), endDate.getMonth(), 1);

  while (cursor <= last) {
    const value = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}`;
    options.push({
      value,
      label: monthFormatter.format(cursor),
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }

  return options;
}

/**
 * Build recent month options ending at the current month (newest first).
 *
 * @param {number} [count=12] Number of months to include.
 * @returns {Array<{value: string, label: string, month: string, monthLabel: string, year: string}>}
 */
export function getRecentMonthOptions(count = 12) {
  const monthFormatter = new Intl.DateTimeFormat("en-US", {
    month: "long",
  });
  const fullFormatter = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
  });
  const now = today();

  return Array.from({ length: count }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    return {
      value,
      label: fullFormatter.format(date),
      month: String(date.getMonth() + 1).padStart(2, "0"),
      monthLabel: monthFormatter.format(date),
      year: String(date.getFullYear()),
    };
  });
}
